import { toneDot, toneText } from "@/lib/statusTone";
import type { SidebarWorker } from "./types";

type Counts = NonNullable<SidebarWorker["attentionCounts"]>;

export function attentionSummary(counts: Counts): string {
  return [
    counts.errors > 0 ? `${counts.errors} ${counts.errors === 1 ? "error" : "errors"}` : null,
    counts.opportunities > 0 ? `${counts.opportunities} ${counts.opportunities === 1 ? "needs" : "need"} action` : null,
    counts.neutral > 0 ? `${counts.neutral} ${counts.neutral === 1 ? "update" : "updates"}` : null,
  ].filter(Boolean).join("; ");
}

/** Only the highest current priority is visible; details remain in the tooltip. */
export function AttentionIndicator({ counts, variant = "dot", healthy = false }: {
  counts: Counts;
  variant?: "dot" | "hearts";
  healthy?: boolean;
}) {
  const level = counts.errors > 0 ? "error" : counts.opportunities > 0 ? "opportunity"
    : variant === "hearts" && healthy ? "healthy" : null;
  if (!level) return null;
  const label = level === "healthy" ? "Healthy — no errors or action needed" : attentionSummary(counts);
  const tone = level === "error" ? "danger" : level === "opportunity" ? "attention" : "success";
  const filledHearts = level === "error" ? 1 : level === "opportunity" ? 2 : 3;
  return <span role="group" data-attention-level={level === "healthy" ? undefined : level}
    data-worker-health={level === "healthy" ? "healthy" : undefined}
    data-worker-health-hearts={variant === "hearts" ? "true" : undefined} title={label} aria-label={label}
    className={variant === "hearts" ? "flex h-3.5 w-8 shrink-0 items-center gap-px" : "flex size-3.5 shrink-0 items-center justify-center"}>
    {variant === "hearts" ? [0, 1, 2].map(index => (
      <svg key={index} aria-hidden="true" viewBox="0 0 16 16" fill="currentColor" className="size-[9px] shrink-0">
        <path d="M8 14 2.3 8.8C-1 5.6 2.1 0.6 6 3.2L8 5l2-1.8C13.9 0.6 17 5.6 13.7 8.8Z"
          className={index < filledHearts ? toneText[tone] : "text-zinc-700"} />
      </svg>
    )) : <span aria-hidden="true" className={`size-1.5 rounded-full ${toneDot[tone]}`} />}
  </span>;
}
